import {
  Component,
  OnInit,
  ViewChild,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { HttpEventType } from '@angular/common/http';
import { EMPTY, catchError, filter, finalize, first, switchMap, tap } from 'rxjs';
import { ToolbarComponent } from '../../core/layout/components/toolbar/toolbar.component';
import { FooterComponent } from '../../core/layout/components/footer/footer.component';
import {
  AzTableComponent,
  AzTableColumn,
  AzTableAction,
} from '../../shared/components/az-table/az-table.component';
import { AzButtonComponent } from '../../shared/components/az-button/az-button.component';
import { AzDocumentUpdateModalComponent } from '../../shared/components/az-document-update-modal/az-document-update-modal.component';
import { StatusChangeModalComponent } from '../../shared/components/status-change-modal/status-change-modal.component';
import { DocumentRepositoryService } from './services/document-repository.service';
import { DocumentEntry } from './models/document-entry.model';
import { LoggerService } from '../../core/logging/logger.service';
import { UserPermissionsService } from '../../core/auth/services/user-permissions.service';

export interface DocumentDisplayRow {
  name: string;
  uploadDate: string;
  uploadedBy: string;
}

@Component({
  selector: 'app-document-repository',
  imports: [
    ToolbarComponent,
    FooterComponent,
    AzTableComponent,
    AzButtonComponent,
    AzDocumentUpdateModalComponent,
    StatusChangeModalComponent,
  ],
  providers: [DatePipe],
  templateUrl: './document-repository.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './document-repository.component.scss',
})
export class DocumentRepositoryComponent implements OnInit {
  private datePipe = inject(DatePipe);
  private router = inject(Router);
  readonly userPermissionsService = inject(UserPermissionsService);

  @ViewChild('uploadStatusModal') statusModal!: StatusChangeModalComponent;
  @ViewChild(AzDocumentUpdateModalComponent) documentUpdateModal!: AzDocumentUpdateModalComponent;

  loading = signal(false);
  loadError = signal<string | null>(null);
  isStatusModalOpen = signal(false);
  isProcessing = signal(false);
  statusModalTitle = signal('Document Repository');
  statusModalProcessingTitle = signal('Loading...');
  documents: DocumentEntry[] = [];
  showUploadModal = signal(false);

  readonly documentTypes = [
    'Binding Corporate Rules EU',
    'Binding Corporate Rules UK',
    'Data Privacy Standard EU',
    'Data Privacy Standard UK',
  ];

  columns: AzTableColumn[] = [
    { key: 'name', label: 'Document' },
    { key: 'uploadedBy', label: 'Uploaded By' },
    { key: 'uploadDate', label: 'Upload Date' },
  ];

  actions: AzTableAction<DocumentDisplayRow>[] = [
    {
      icon: 'download',
      label: 'Download',
      callback: (row: DocumentDisplayRow) => this.onDownload(row),
    },
  ];

  get displayDocuments(): DocumentDisplayRow[] {
    return this.documents.map((doc) => ({
      name: doc.name,
      uploadDate: this.datePipe.transform(doc.uploadDate, 'dd/MMM/yyyy', 'UTC') ?? '',
      uploadedBy: doc.uploadedBy || '--',
    }));
  }

  get canReplaceFile(): boolean {
    return (
      this.userPermissions.hasDocumentRepositoryUploadPermission() &&
      this.userPermissions.hasPstMemberRole()
    );
  }

  constructor(
    private documentRepositoryService: DocumentRepositoryService,
    private logger: LoggerService,
    private userPermissions: UserPermissionsService,
  ) {}

  ngOnInit(): void {
    this.logger.info('Document Repository page loaded');
    this.loadDocuments();
  }

  toSlug(documentName: string): string {
    return documentName.toLowerCase().replace(/ /g, '-');
  }

  onDocumentRowClick(row: DocumentDisplayRow): void {
    if (this.userPermissionsService.hasPstMemberRole()) {
      this.router.navigate(['/document-repository', this.toSlug(row.name), 'contacts']);
    }
  }

  openUploadModal(): void {
    if (!this.canReplaceFile) {
      this.showReplacementError('Only PST Members can replace documents.');
      return;
    }
    if (!this.isProcessing()) {
      this.showUploadModal.set(true);
    }
  }

  closeUploadModal(): void {
    this.showUploadModal.set(false);
  }

  closeStatusModal(): void {
    this.isStatusModalOpen.set(false);
    this.documentUpdateModal?.resetForm();
  }

  onDownload(row: DocumentDisplayRow): void {
    this.statusModalTitle.set('Download Document');
    this.statusModalProcessingTitle.set('Generating download link...');
    this.isStatusModalOpen.set(true);

    this.documentRepositoryService.getDownloadUrl(row.name).subscribe({
      next: (res) => {
        this.isStatusModalOpen.set(false);
        window.open(res.download_url, '_blank');
      },
      error: (err) => {
        this.logger.error('Failed to get download URL', err);
        const message = err?.error?.message || err?.message || 'Failed to generate download URL.';
        this.statusModal.showError(message, 'Download Failed', 'error');
      },
    });
  }

  onUpload(payload: { file: File | null; documentType: string; comments: string }): void {
    if (this.isProcessing()) return;
    if (!this.canReplaceFile) {
      this.showReplacementError('Only PST Members can replace documents.');
      return;
    }
    const file = payload.file;
    if (
      !file ||
      !this.documentTypes.includes(payload.documentType) ||
      !file.name.toLowerCase().endsWith('.pdf') ||
      file.size <= 0 ||
      file.size > this.documentUpdateModal.MAX_FILE_SIZE
    ) {
      this.showReplacementError(
        'Select an existing document and a nonempty PDF within the upload size limit.',
      );
      return;
    }

    this.isProcessing.set(true);
    this.closeUploadModal();
    this.statusModalTitle.set('Document Repository');
    this.statusModalProcessingTitle.set('Replacing document...');
    this.statusModal.state = 'processing';
    this.isStatusModalOpen.set(true);
    let stage = 'prepare';
    this.documentRepositoryService
      .getPresignedUrl()
      .pipe(
        switchMap(({ file: target }) => {
          stage = 'upload';
          return this.documentRepositoryService.uploadToS3(file, target.presigned_post).pipe(
            filter((event) => event.type === HttpEventType.Response),
            first(),
            switchMap(() => {
              stage = 'confirm';
              return this.documentRepositoryService.confirmUpload({
                document_type: payload.documentType,
                file_s3_key: target.s3_key,
              });
            }),
          );
        }),
        tap(() => {
          this.statusModal.showSuccessWithMessage(
            'Document replaced successfully.',
            'Replace File',
          );
          this.loading.set(true);
          this.loadError.set(null);
        }),
        switchMap(() =>
          this.documentRepositoryService.getDocuments().pipe(
            tap((docs) => {
              this.documents = docs;
            }),
            catchError(() => {
              const message =
                'Document replaced, but the table could not be refreshed. Reload the page to see the latest metadata.';
              this.logger.error('Document replacement committed; table refresh failed');
              this.loadError.set(message);
              this.statusModal.showError(message, 'Refresh Failed', 'error');
              return EMPTY;
            }),
          ),
        ),
        catchError(() => {
          const message =
            stage === 'confirm'
              ? 'Unable to confirm replacement. Reload the page to check the current document before trying again.'
              : 'Failed to upload replacement file. Please try again.';
          this.logger.error(`Document replacement failed during ${stage}`);
          this.statusModal.showError(message, 'Upload Failed', 'error');
          return EMPTY;
        }),
        finalize(() => {
          this.isProcessing.set(false);
          this.loading.set(false);
        }),
      )
      .subscribe();
  }

  private showReplacementError(message: string): void {
    this.logger.error(message);
    this.isStatusModalOpen.set(true);
    this.statusModal.showError(message, 'Replace File', 'error');
  }

  private loadDocuments(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.documentRepositoryService.getDocuments().subscribe({
      next: (docs) => {
        this.documents = docs;
        this.loading.set(false);
      },
      error: (err) => {
        this.logger.error('Failed to load document repository', err);
        this.loadError.set('Failed to load documents. Please try again.');
        this.loading.set(false);
      },
    });
  }
}
